
import { Component, Prop, Vue } from "vue-property-decorator"
import { Field, CalculationType } from "@/types/field"
import { FieldInstance } from "@/types"
import cloneDeep from "lodash/cloneDeep"
import { caseColumns } from "@/types/caseColumns"

@Component({
	components: {

	}
})
export default class SectionedFieldSelector extends Vue {
	@Prop({ type: Object, default: undefined })
	value!: { [key: string]: string[] } // v-model is optional

	@Prop()
	sectionFields!: { [section: string]: FieldInstance[] }

	/* Filters the fields that are presented as options */
	@Prop({ default: () => () => true })
	fieldOptionsFilter!: (field: FieldInstance) => boolean

	@Prop({ type: Boolean, default: false })
	selectAllByDefault!: boolean

	// Use modelValue to get/set the value of the component
	private internalValue: { [key: string]: string[] } = {}

	private mounted () {
		if (this.selectAllByDefault) {
			this.modelValue = Object.entries(this.filteredSectionFields)
				.reduce((acc: { [section: string]: string[]}, [section, fields]) => {
					acc[section] = fields.filter(this.fieldOptionsFilter).map(f => f.field.id!)
					return acc
				}, {})
		}
	}

	private get modelValue (): { [key: string]: string[] } {
		if (this.value !== undefined) {
			return this.value
		} else {
			return this.internalValue
		}
	}

	private set modelValue (value: { [key: string]: string[] }) {
		const newValue = Object.fromEntries(
			Object.entries(value).filter(([key, value]) => value && value.length > 0)
		)

		if (this.value !== undefined) {
			this.$emit("input", newValue)
		} else {
			this.internalValue = newValue
		}
	}

	private get filteredSectionFields (): { [section: string]: FieldInstance[] } {
		return Object.entries(this.sectionFields)
			.reduce((acc: { [section: string]: FieldInstance[]}, [section, fields]) => {
				const filteredFields = fields.filter(this.fieldOptionsFilter)
				if (filteredFields.length > 0) {
					acc[section] = filteredFields
				}
				return acc
			}, {})
	}

	private selectAllSection (section: string, checked: boolean) {
		const updatedFields = Object.values(this.filteredSectionFields[section])
		if (checked) {
			Vue.set(this.modelValue, section, updatedFields.map(f => f.field.id!))
			updatedFields.forEach(f => this.fieldSelectionChanged(f, [f.field.id!]))
		} else {
			Vue.set(this.modelValue, section, [])
			updatedFields.forEach(f => this.fieldSelectionChanged(f, []))
		}
	}

	private selectAll (checked: boolean) {
		if (checked) {
			this.modelValue = Object.entries(this.filteredSectionFields)
				.reduce((acc: { [section: string]: string[]}, [section, fields]) => {
					acc[section] = fields.map(f => f.field.id!)
					return acc
				}, {})
		} else {
			this.modelValue = {}
		}
	}

	private get allSelected (): boolean {
		return Object.keys(this.modelValue).length === Object.keys(this.filteredSectionFields).length &&
			Object.keys(this.modelValue).every(section => this.modelValue[section].length === this.filteredSectionFields[section].length)
	}

	private get allFields (): FieldInstance[] {
		return Object.keys(this.filteredSectionFields).reduce((acc, section) => acc.concat(this.filteredSectionFields[section]), [] as FieldInstance[])
	}

	public get selectedFields (): string[] {
		return Object.keys(this.modelValue).reduce((acc, section) => acc.concat(this.modelValue[section]), [] as string[])
	}

	public getValue (): { [section: string]: string[] } {
		return cloneDeep(this.modelValue)
	}

	getFieldDataTypeDisplayString (field: FieldInstance): string {
		return field.field.dataType !== "calculated"
			? field.field.dataType ?? "(Unknown)"
			: `calculated - ${CalculationType[field.field.extensions?.calculationType].split(/(?=[A-Z])/).join(" ")}`
	}

	getFieldDependenciesString (field: FieldInstance): string | null {
		if (field.field.dataType !== "calculated") return null

		const dependencies = field.field.extensions?.fields?.filter((fieldId: string) =>
			(!!fieldId && !caseColumns.some(c => c.key === fieldId))
		) as string[] | undefined

		if (!dependencies) return null

		return `'${dependencies.map(d => this.allFields.find(f => f.field.id === d)?.field.title ?? d).join("', '")}'`
	}

	private fieldSelectionChanged (field: FieldInstance, checked: string[]) {
		const fieldSelected = checked.includes(field.field.id!)
		if (fieldSelected) {
			const dependencies = field.field.extensions?.fields?.filter((fieldId: string) =>
				(!!fieldId && !caseColumns.some(c => c.key === fieldId))
			) as string[] | undefined

			if (dependencies) {
				// Ensure that all the Fields' dependencies are selected
				// Call this method recursively to ensure all dependencies are selected
				dependencies.forEach(d => {
					const dependentField = this.allFields.find(f => f.field.id === d)
					if (dependentField && !this.selectedFields.includes(dependentField.field.id!)) {
						this.modelValue = {
							...this.modelValue,
							[dependentField.section]: [...(this.modelValue[dependentField.section] ?? []), dependentField.field.id!]
						}

						this.fieldSelectionChanged(dependentField, [dependentField.field.id!])
					}
				})
			}
		} else {
			// Deselect any Fields dependent on this one
			// Call this method recursively to ensure all dependencies are deselected
			const dependentFields =
				this.allFields.filter(f => f.field.extensions?.fields?.includes(field.field.id!))

			dependentFields.filter(f => this.selectedFields.includes(f.field.id!)).forEach(f => {
				this.modelValue = {
					...this.modelValue,
					[f.section]: (this.modelValue[f.section] ?? []).filter(id => id !== f.field.id)
				}

				this.fieldSelectionChanged(f, [])
			})
		}
	}
}
